import {
  BILLING_FREQUENCY_IDS,
  BILLING_PAYMENT_TIMING_IDS,
} from "./trainer-stammdaten-reference-lists.js";

function asStr(v) {
  return String(v ?? "").trim();
}

export const DEFAULT_BILLING_FREQUENCY = "monthly";
export const DEFAULT_BILLING_PAYMENT_TIMING = "arrears";

export const BILLING_SETTINGS_SOURCE_TRAINER = "trainer";
export const BILLING_SETTINGS_SOURCE_CUSTOM = "custom";

const BUILTIN_FREQUENCY_SET = new Set(BILLING_FREQUENCY_IDS);
const BUILTIN_TIMING_SET = new Set(BILLING_PAYMENT_TIMING_IDS);

/** @param {unknown} opts */
function normalizeBillingSettingsContext(opts) {
  if (!opts || typeof opts !== "object") {
    return { trainer: null, tenantReferenceLists: null, sessionDateIso: "" };
  }
  if ("trainer" in opts || "sessionDateIso" in opts) {
    return {
      trainer: opts.trainer ?? null,
      tenantReferenceLists: opts.tenantReferenceLists ?? null,
      sessionDateIso: asStr(opts.sessionDateIso).slice(0, 10),
    };
  }
  return {
    trainer: null,
    tenantReferenceLists: opts,
    sessionDateIso: "",
  };
}

/** @param {unknown} trainer */
function trainerBillingSnapshotAtDate(trainer, sessionDateIso) {
  const effectiveFrom = asStr(trainer?.billing_settings_effective_from).slice(
    0,
    10,
  );
  const previous = trainer?.billing_settings_previous;
  const date = asStr(sessionDateIso).slice(0, 10);
  if (
    effectiveFrom &&
    /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) &&
    date &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    date < effectiveFrom &&
    previous &&
    typeof previous === "object"
  ) {
    return {
      billing_frequency: asStr(previous.billing_frequency) || null,
      billing_payment_timing: asStr(previous.billing_payment_timing) || null,
    };
  }
  return {
    billing_frequency: asStr(trainer?.billing_frequency) || null,
    billing_payment_timing: asStr(trainer?.billing_payment_timing) || null,
  };
}

/**
 * @param {unknown} person
 * @param {unknown} [trainer]
 */
export function isRecipientBillingSettingsCustomized(person, trainer = null) {
  const source = asStr(person?.billing_settings_source);
  if (source === BILLING_SETTINGS_SOURCE_CUSTOM) return true;
  if (source === BILLING_SETTINGS_SOURCE_TRAINER) return false;
  if (!trainer) return false;
  const snap = trainerBillingSnapshotAtDate(trainer, "");
  const personFreq = asStr(person?.billing_frequency);
  const personTiming = asStr(person?.billing_payment_timing);
  const trainerFreq = asStr(snap.billing_frequency);
  const trainerTiming = asStr(snap.billing_payment_timing);
  if (personFreq && trainerFreq && personFreq !== trainerFreq) return true;
  if (personTiming && trainerTiming && personTiming !== trainerTiming)
    return true;
  return false;
}

/**
 * @param {unknown} trainer
 * @returns {{ billing_frequency: string|null, billing_payment_timing: string|null, billing_settings_source: string, billing_settings_trainer_id: string|null }}
 */
export function buildTrainerBillingDefaultsForRecipient(trainer) {
  const trainerId = asStr(trainer?.id) || null;
  const snap = trainerBillingSnapshotAtDate(trainer, "");
  return {
    billing_frequency: snap.billing_frequency,
    billing_payment_timing: snap.billing_payment_timing,
    billing_settings_source: BILLING_SETTINGS_SOURCE_TRAINER,
    billing_settings_trainer_id: trainerId,
  };
}

/**
 * @param {Record<string, unknown>} payload
 * @param {unknown} trainer
 */
export function applyTrainerBillingDefaultsToPersonPayload(payload, trainer) {
  const base = payload && typeof payload === "object" ? { ...payload } : {};
  const defaults = buildTrainerBillingDefaultsForRecipient(trainer);
  return {
    ...base,
    ...defaults,
  };
}

/** @param {unknown} value */
function isTrainerBillingRow(value) {
  if (!value || typeof value !== "object") return false;
  return (
    Boolean(asStr(value.id)) &&
    ("billing_frequency" in value ||
      "billing_payment_timing" in value ||
      "hourly_rates" in value)
  );
}

/**
 * @param {unknown} person
 * @param {unknown} [trainerOrLists]
 * @param {unknown} [contextOrLists]
 */
export function resolveEffectiveBillingFrequency(
  person,
  trainerOrLists = null,
  contextOrLists = null,
) {
  const ctx = isTrainerBillingRow(trainerOrLists)
    ? normalizeBillingSettingsContext({
        trainer: trainerOrLists,
        tenantReferenceLists: contextOrLists,
      })
    : normalizeBillingSettingsContext(trainerOrLists);
  void ctx.tenantReferenceLists;
  if (
    asStr(person?.billing_settings_source) === BILLING_SETTINGS_SOURCE_CUSTOM
  ) {
    const custom = asStr(person?.billing_frequency);
    if (custom) return custom;
  }
  const snap = trainerBillingSnapshotAtDate(ctx.trainer, ctx.sessionDateIso);
  const trainerFreq = asStr(snap.billing_frequency);
  if (trainerFreq) return trainerFreq;
  const personFreq = asStr(person?.billing_frequency);
  if (personFreq) return personFreq;
  return DEFAULT_BILLING_FREQUENCY;
}

/**
 * @param {unknown} person
 * @param {unknown} [trainerOrLists]
 * @param {unknown} [contextOrLists]
 */
export function resolveEffectiveBillingPaymentTiming(
  person,
  trainerOrLists = null,
  contextOrLists = null,
) {
  const ctx = isTrainerBillingRow(trainerOrLists)
    ? normalizeBillingSettingsContext({
        trainer: trainerOrLists,
        tenantReferenceLists: contextOrLists,
      })
    : normalizeBillingSettingsContext(trainerOrLists);
  void ctx.tenantReferenceLists;
  if (
    asStr(person?.billing_settings_source) === BILLING_SETTINGS_SOURCE_CUSTOM
  ) {
    const custom = asStr(person?.billing_payment_timing);
    if (custom) return custom;
  }
  const snap = trainerBillingSnapshotAtDate(ctx.trainer, ctx.sessionDateIso);
  const trainerTiming = asStr(snap.billing_payment_timing);
  if (trainerTiming) return trainerTiming;
  const personTiming = asStr(person?.billing_payment_timing);
  if (personTiming) return personTiming;
  return DEFAULT_BILLING_PAYMENT_TIMING;
}

/**
 * @param {{ person?: unknown, trainer?: unknown, sessionDateIso?: string }} opts
 */
export function resolveBillingPreparePolicy(opts = {}) {
  const timing = resolveEffectiveBillingPaymentTiming(opts.person, {
    trainer: opts.trainer ?? null,
    sessionDateIso: opts.sessionDateIso,
  });
  if (timing === "advance") {
    return {
      defaultIncludeCurrentMonthSessions: true,
      shouldPromptForCurrentMonth: false,
      paymentTiming: timing,
    };
  }
  return {
    defaultIncludeCurrentMonthSessions: false,
    shouldPromptForCurrentMonth: true,
    paymentTiming: timing,
  };
}

/**
 * @param {{ row?: object, people?: object[], billingContacts?: object[] }} input
 */
export function resolveBillingRecipientPersonFromWorkQueueRow(input) {
  const row = input?.row && typeof input.row === "object" ? input.row : {};
  const people = Array.isArray(input?.people) ? input.people : [];
  const rt = asStr(row.recipientType).toLowerCase();
  if (rt === "contact") {
    const cid = asStr(row.headerBillingContactId);
    if (!cid) return null;
    return (
      people.find(
        (p) =>
          asStr(p?.id) === cid &&
          asStr(p?.contact_source).toLowerCase() === "billing",
      ) ||
      people.find((p) => asStr(p?.id) === cid) ||
      null
    );
  }
  const pid = asStr(row.headerPersonId);
  if (!pid) return null;
  return people.find((p) => asStr(p?.id) === pid) || null;
}

/**
 * @param {{ person?: object|null, trainers?: object[], coordinatorTrainerId?: string, billingSettingsTrainerId?: string }} input
 */
export function resolveBillingSettingsTrainerForRecipient(input) {
  const trainers = Array.isArray(input?.trainers) ? input.trainers : [];
  const byId = new Map(trainers.map((tr) => [asStr(tr?.id), tr]));
  const fromPerson = asStr(input?.person?.billing_settings_trainer_id);
  if (fromPerson && byId.has(fromPerson)) return byId.get(fromPerson);
  const explicit = asStr(input?.billingSettingsTrainerId);
  if (explicit && byId.has(explicit)) return byId.get(explicit);
  const coord = asStr(input?.coordinatorTrainerId);
  if (coord && byId.has(coord)) return byId.get(coord);
  return trainers[0] || null;
}

/**
 * Payload-Markierung bei manueller Empfänger-Änderung.
 *
 * @param {Record<string, unknown>} payload
 * @param {Record<string, unknown>} existing
 * @param {Record<string, unknown>} trainerDefaults
 */
export function markRecipientBillingSettingsPayload(
  payload,
  existing,
  trainerDefaults = {},
) {
  const next = payload && typeof payload === "object" ? { ...payload } : {};
  const prev = existing && typeof existing === "object" ? existing : {};
  const touched =
    ("billing_frequency" in next &&
      asStr(next.billing_frequency) !== asStr(prev.billing_frequency)) ||
    ("billing_payment_timing" in next &&
      asStr(next.billing_payment_timing) !==
        asStr(prev.billing_payment_timing));
  if (!touched) return next;
  const freq = asStr(next.billing_frequency);
  const timing = asStr(next.billing_payment_timing);
  const trainerFreq = asStr(trainerDefaults.billing_frequency);
  const trainerTiming = asStr(trainerDefaults.billing_payment_timing);
  const matchesTrainer =
    (!freq || !trainerFreq || freq === trainerFreq) &&
    (!timing || !trainerTiming || timing === trainerTiming);
  const exactTrainerMatch =
    freq === trainerFreq &&
    timing === trainerTiming &&
    trainerFreq &&
    trainerTiming;
  if (exactTrainerMatch) {
    next.billing_settings_source = BILLING_SETTINGS_SOURCE_TRAINER;
    if (trainerDefaults.billing_settings_trainer_id) {
      next.billing_settings_trainer_id =
        trainerDefaults.billing_settings_trainer_id;
    }
    return next;
  }
  if (matchesTrainer && !freq && !timing) return next;
  next.billing_settings_source = BILLING_SETTINGS_SOURCE_CUSTOM;
  return next;
}

/** @param {string} ym */
export function ymIsValidBillingMonth(ym) {
  return /^\d{4}-\d{2}$/.test(asStr(ym));
}

/** @param {string} ym @param {string} capYm */
export function ymIsNotAfterBillingMonth(ym, capYm) {
  const a = asStr(ym);
  const b = asStr(capYm);
  if (!ymIsValidBillingMonth(a) || !ymIsValidBillingMonth(b)) return false;
  return a <= b;
}

/**
 * @param {string} ym
 * @returns {{ start: string, end: string }}
 */
export function ymQuarterRangeInclusive(ym) {
  const m = asStr(ym).match(/^(\d{4})-(\d{2})$/);
  if (!m) return { start: "", end: "" };
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10) - 1;
  if (!Number.isFinite(y) || !Number.isFinite(mo))
    return { start: "", end: "" };
  const q = Math.floor(mo / 3);
  const startMo = q * 3;
  const endMo = startMo + 2;
  return {
    start: `${y}-${String(startMo + 1).padStart(2, "0")}`,
    end: `${y}-${String(endMo + 1).padStart(2, "0")}`,
  };
}

/** @param {string} ym */
export function ymToQuarterPeriodKey(ym) {
  const m = asStr(ym).match(/^(\d{4})-(\d{2})$/);
  if (!m) return "";
  const y = m[1];
  const mo = parseInt(m[2], 10);
  if (!Number.isFinite(mo)) return "";
  const q = Math.ceil(mo / 3);
  return `${y}-Q${q}`;
}

/** @param {string} ym */
export function isQuarterClosingYm(ym) {
  const mo = parseInt(asStr(ym).slice(5, 7), 10);
  return Number.isFinite(mo) && mo % 3 === 0;
}

/** @param {unknown} session */
export function sessionDateToYm(session) {
  const raw = asStr(session?.date);
  const ym = raw.slice(0, 7);
  return ymIsValidBillingMonth(ym) ? ym : "";
}

/**
 * @param {string} frequencyId
 */
export function isKnownBillingFrequencyId(frequencyId) {
  const id = asStr(frequencyId);
  if (!id) return false;
  if (BUILTIN_FREQUENCY_SET.has(id)) return true;
  return id.startsWith("billing_frequency_custom_");
}

/**
 * @param {string} timingId
 */
export function isKnownBillingPaymentTimingId(timingId) {
  const id = asStr(timingId);
  if (!id) return false;
  if (BUILTIN_TIMING_SET.has(id)) return true;
  return id.startsWith("billing_timing_custom_");
}

/**
 * Soll eine bestätigte Session in der Abrechnungs-Inbox für `viewPeriodMonth` erscheinen?
 *
 * @param {{ session: unknown, person: unknown, viewPeriodMonth: string, tenantReferenceLists?: unknown }} opts
 */
export function shouldIncludeSessionForBillingPeriod(opts) {
  const session = opts?.session;
  const person = opts?.person;
  const viewPeriodMonth = asStr(opts?.viewPeriodMonth);
  const sessionYm = sessionDateToYm(session);
  if (!sessionYm || !ymIsValidBillingMonth(viewPeriodMonth)) return false;
  if (!ymIsNotAfterBillingMonth(sessionYm, viewPeriodMonth)) return false;

  const freq = resolveEffectiveBillingFrequency(
    person,
    opts?.tenantReferenceLists,
  );

  if (
    freq === "after_session" ||
    freq === "monthly" ||
    !isKnownBillingFrequencyId(freq)
  ) {
    return sessionYm === viewPeriodMonth;
  }

  if (freq === "quarterly") {
    if (!isQuarterClosingYm(viewPeriodMonth)) return false;
    const { start, end } = ymQuarterRangeInclusive(viewPeriodMonth);
    if (!start || !end) return false;
    return sessionYm >= start && sessionYm <= end;
  }

  return sessionYm === viewPeriodMonth;
}

/**
 * Aggregations-Schlüssel für Rechnungen je Empfänger und Rhythmus.
 *
 * @param {{ frequency?: string, session?: unknown, viewPeriodMonth?: string }} opts
 */
export function resolveRecipientBillingPeriodKey(opts = {}) {
  const session = opts?.session;
  const viewPeriodMonth = asStr(opts?.viewPeriodMonth);
  const sessionYm = sessionDateToYm(session);
  const freq = asStr(opts?.frequency) || DEFAULT_BILLING_FREQUENCY;
  const sessionId = asStr(session?.id);

  if (freq === "after_session") {
    return sessionId ? `sess:${sessionId}` : sessionYm || viewPeriodMonth;
  }
  if (freq === "quarterly") {
    return (
      ymToQuarterPeriodKey(sessionYm || viewPeriodMonth) || viewPeriodMonth
    );
  }
  return sessionYm || viewPeriodMonth;
}

/**
 * @param {string} isoDate YYYY-MM-DD
 * @param {number} days
 */
export function addDaysToIsoDate(isoDate, days) {
  const raw = asStr(isoDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  const dt = new Date(`${raw}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().slice(0, 10);
}

/**
 * @param {{ paymentTiming?: string, sessionDates?: string[], referenceDate?: string }} opts
 * @returns {string}
 */
export function computeBillingStatementDueDate(opts = {}) {
  const dates = (Array.isArray(opts.sessionDates) ? opts.sessionDates : [])
    .map((d) => asStr(d).slice(0, 10))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    .sort();
  const timing = asStr(opts.paymentTiming) || DEFAULT_BILLING_PAYMENT_TIMING;
  const fallback = asStr(opts.referenceDate).slice(0, 10);

  if (!dates.length) return fallback || "";

  if (timing === "advance") {
    return addDaysToIsoDate(dates[0], -14) || fallback || "";
  }

  return addDaysToIsoDate(dates[dates.length - 1], 14) || fallback || "";
}

/**
 * @param {unknown[]} items
 * @param {Map<string, unknown>} peopleById
 * @param {unknown} [tenantReferenceLists]
 */
export function resolveBillingPaymentTimingForStatementItems(
  items,
  peopleById,
  tenantReferenceLists = null,
) {
  const rows = Array.isArray(items) ? items : [];
  let sawAdvance = false;
  let sawArrears = false;

  for (const item of rows) {
    const personId = asStr(item?.person_id);
    const person = peopleById?.get?.(personId) || null;
    const timing = resolveEffectiveBillingPaymentTiming(
      person,
      tenantReferenceLists,
    );
    if (timing === "advance") sawAdvance = true;
    else sawArrears = true;
  }

  if (sawAdvance && !sawArrears) return "advance";
  return DEFAULT_BILLING_PAYMENT_TIMING;
}
